/**
 * M10.5: admin API client. Critical actions need a fresh password confirmation: the server answers
 * `reauth_required`, the UI asks for the password once, the 5-minute token is kept in memory only.
 */
import { apiFetch, ApiError } from './client.ts';
import { getAccessToken } from './session.ts';

let reauth: { token: string; expiresAt: number } | null = null;

export function hasReauth(): boolean {
  return !!reauth && reauth.expiresAt - Date.now() > 10_000;
}

export async function reauthenticate(password: string): Promise<void> {
  const r = await apiFetch<{ token: string; expiresAt: string }>('/admin/reauth', { method: 'POST', body: JSON.stringify({ password }) });
  reauth = { token: r.token, expiresAt: Date.parse(r.expiresAt) };
}

/** Runs a critical call; if the server wants re-authentication, `ask` gets the password first. */
export async function critical<T>(path: string, body: unknown, ask: () => Promise<string | null>, method = 'POST'): Promise<T> {
  const call = () => apiFetch<T>(path, {
    method, body: JSON.stringify(body ?? {}),
    headers: hasReauth() ? { 'X-Admin-Reauth': reauth!.token } : {},
  });
  try {
    return await call();
  } catch (e) {
    if (!(e instanceof ApiError) || e.code !== 'reauth_required') throw e;
    const pw = await ask();
    if (!pw) throw new Error('Megszakítva.');
    await reauthenticate(pw);
    return call();
  }
}

export interface AdminAlert { id: string; level: 'warning' | 'critical'; message: string }

export interface AdminOverview {
  server: {
    at: string;
    api: { uptimeS: number; version: string; nodeEnv: string; pid: number };
    db: { ok: boolean; latencyMs: number };
    worker: { running: boolean; lastTickAt: string | null; lastTickMs: number; lastError: string | null; stale: boolean };
    websockets: number;
    memoryMb: { rss: number; heapUsed: number; heapTotal: number };
    events: { pending: number; processing: number; failed: number; failed24h: number; completed24h: number; overdue: number; oldestOverdueLagMs: number };
    mail: { transport: string; sent: number; failed: number; consecutiveFailures: number; lastSuccessAt: string | null; lastFailureAt: string | null; lastError: string | null; configProblem: string | null; verifiedAt: string | null };
  };
  world: AdminWorldStats | null;
  alerts: AdminAlert[];
}

export interface AdminWorldStats {
  world: { id: string; name: string; seed: string };
  cachedAt: string;
  systems: number; owned: number; free: number; ownedRatio: number;
  civilizations: number; npcCivilizations: number; players: number; activePlayers15m: number; activePlayers24h: number;
  activeBattles: number; activeFleets: number; activeConflicts: number;
  startingCapacity: { eligible: number; preferred: number };
}

export interface AdminWorld extends AdminWorldStats {
  capacityWarnings: string[];
  dataAudit: {
    sharedHomes: Array<{ system_id: string; system_name: string; civs: string[] }>;
    homesNotOwned: Array<{ civ_id: string; civ_name: string; system_name: string; owner_name: string | null }>;
    duplicateNames: Array<{ name: string; count: number }>;
    fleetsOutsideOwnWorld: number; issueCount: number;
  };
  totals: Record<string, number>;
  expansions: Array<{ id: string; seq: number; side: string; systemsAdded: number; lanesAdded: number; createdAt: string }>;
  backups: Array<{ id: string; fileName: string; sizeBytes: number; status: string; createdAt: string; databaseName: string }>;
}

export interface ExpansionPlan {
  worldId: string; worldName: string; seq: number; side: string; planHash: string;
  systemsToAdd: number; regionLanesToAdd: number; connectorLanesToAdd: number;
  connectors: Array<{ from: string; to: string; distance: number }>;
  region: { x: number; y: number; width: number; height: number }; existingSystems: number;
  capacity: { before: { eligible: number; preferred: number }; after: { eligible: number; preferred: number } };
  risks: string[];
}

export interface AdminPlayerRow { id: string; username: string; email: string | null; role: string; emailVerified: boolean; createdAt: string; lastSeenAt: string | null; isNpc: boolean; civName: string | null }
export interface AdminPlayerDetail {
  player: { id: string; username: string; email: string | null; role: string; isNpc: boolean; isDemoAccount: boolean; emailVerified: boolean; emailVerifiedAt: string | null; createdAt: string; lastSeenAt: string | null; passwordChangedAt: string | null };
  civilization: { id: string; name: string; color: string; emblem: string; createdAt: string } | null;
  sessions: Array<{ id: string; device: string; ip: string | null; createdAt: string; lastUsedAt: string; expiresAt: string }>;
}
export interface AdminEvent { id: string; type: string; status: string; dueAt: string; attempts: number; maxAttempts: number; lastError: string | null; createdAt: string; completedAt: string | null; lagMs: number | null; retryable: boolean }
export interface AdminEventDetail extends AdminEvent { payload: Record<string, unknown>; retry: { allowed: boolean; reason: string | null } }
export interface AuditEntry { id: string; actorLabel: string; action: string; targetType: string | null; targetId: string | null; details: Record<string, unknown>; ip: string | null; createdAt: string }

export const fetchOverview = () => apiFetch<AdminOverview>('/admin/overview');
export const fetchWorld = (fresh = false) => apiFetch<AdminWorld>(`/admin/world${fresh ? '?fresh=1' : ''}`);
export const searchPlayers = (q: string) => apiFetch<{ players: AdminPlayerRow[] }>(`/admin/players?q=${encodeURIComponent(q)}`);
export const fetchPlayer = (id: string) => apiFetch<AdminPlayerDetail>(`/admin/players/${id}`);
export const dryRunExpansion = (b: { systemCount: number; side: string; connectors?: number }) =>
  apiFetch<ExpansionPlan>('/admin/world/expansion/dry-run', { method: 'POST', body: JSON.stringify(b) });
export const fetchEventSummary = () => apiFetch<{ counts: Record<string, number>; failedByType: Array<{ event_type: string; n: number }>; overdue: number; oldestOverdueLagMs: number }>('/admin/events/summary');
export const fetchEvents = (f: { status?: string; type?: string; q?: string }) =>
  apiFetch<{ events: AdminEvent[] }>(`/admin/events?${new URLSearchParams(Object.entries(f).filter(([, v]) => v) as [string, string][]).toString()}`);
export const fetchEvent = (id: string) => apiFetch<AdminEventDetail>(`/admin/events/${id}`);
export const fetchAudit = () => apiFetch<{ entries: AuditEntry[] }>('/admin/audit?limit=200');
export const verifyMail = () => apiFetch<{ ok: boolean; error?: string }>('/admin/mail/verify', { method: 'POST', body: '{}' });

// ---- M10.6: closed Alpha ----

export type RegistrationMode = 'open' | 'invite' | 'closed';
export interface InviteRow {
  id: string; email: string | null; note: string | null; status: 'active' | 'used' | 'expired' | 'revoked';
  createdAt: string; expiresAt: string; usedAt: string | null; usedByUsername: string | null; revokedAt: string | null; sentAt: string | null;
}
export interface InviteStats { total: number; used: number; revoked: number; expired: number; active: number; sent: number }
export interface AlphaOverview {
  registrationMode: RegistrationMode; defaultRegistrationMode: RegistrationMode; invites: InviteStats;
  players: {
    registered: number; viaInvite: number; verified: number; active15m: number; active24h: number; active7d: number; returning7d: number;
    newCivilizations24h: number; newCivilizations7d: number; verifiedWithoutCivilization: number;
  };
  feedback: Record<string, number>;
  backups: {
    expected: boolean;
    last: { status: string; createdAt: string | null; encrypted: boolean; offsiteAt: string | null; offsiteError: string | null; fileName: string } | null;
    lastVerifiedAt: string | null; lastOffsiteAt: string | null; lastRestoreTest: { ok: boolean; createdAt: string | null; source: string } | null;
  };
  backupAlerts: AdminAlert[];
  serverErrors: { lastHour: number; recent: Array<{ at: string; method: string; path: string; status: number; reqId: string }> };
}
export interface FeedbackRow {
  id: string; category: 'bug' | 'suggestion'; area: string; description: string; tech: Record<string, string>; status: string;
  adminNote: string | null; createdAt: string; updatedAt: string; hasScreenshot: boolean; username: string | null;
}

export const fetchAlpha = () => apiFetch<AlphaOverview>('/admin/alpha');
export const fetchInvites = (status?: string) => apiFetch<{ invites: InviteRow[]; stats: InviteStats }>(`/admin/invites${status ? `?status=${status}` : ''}`);
export const createInvite = (b: { email?: string; expiresInHours: number; note?: string; send: boolean }) =>
  apiFetch<{ invite: InviteRow; token: string; link: string; mailed: boolean | null }>('/admin/invites', { method: 'POST', body: JSON.stringify(b) });
export const revokeInvite = (id: string) => apiFetch<{ ok: true }>(`/admin/invites/${id}/revoke`, { method: 'POST', body: '{}' });
export const fetchFeedback = (f: { status?: string; category?: string; q?: string }) =>
  apiFetch<{ feedback: FeedbackRow[]; stats: Record<string, number> }>(`/admin/feedback?${new URLSearchParams(Object.entries(f).filter(([, v]) => v) as [string, string][]).toString()}`);
export const updateFeedback = (id: string, b: { status: string; adminNote?: string }) =>
  apiFetch<{ ok: true }>(`/admin/feedback/${id}`, { method: 'PATCH', body: JSON.stringify(b) });

/** The screenshot needs the access token, so it is fetched and shown as a blob: URL. */
export async function feedbackScreenshotUrl(id: string): Promise<string> {
  const res = await fetch(`/api/admin/feedback/${id}/screenshot`, { credentials: 'same-origin', headers: { Authorization: `Bearer ${await getAccessToken()}` } });
  if (!res.ok) throw new Error('A képernyőkép nem tölthető be.');
  return URL.createObjectURL(await res.blob());
}
